// PaySimple config for the shared ReconWorkspace (recurring ACH + card, single account). Reads the
// SAME existing /api/manage/paysimple-reconcile* endpoints the old page uses. Single-account PSP, so
// no account dropdown. Its "schedules" are the Subscriptions (vocabulary rule). Mapped subscriptions
// book via the preview dialog; unmapped payments are booked from the Subscriptions tab first.
import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import Button from '@mui/material/Button';
import { get, post } from '../../utils/api';
import { fmtMoney, fmtDate } from '../../components/recon/reconShared';
import { PAYSIMPLE_NAV_ITEMS } from './paysimpleNav';
import PaysimpleCreateOrderDialog from '../../components/recon/paysimple/PaysimpleCreateOrderDialog';
import DonorLink from '../../components/recon/DonorLink';

const bucketOf = (s) => ((s === 'matched' || s === 'needs_link') ? 'order' : s === 'unbooked' ? 'no_order' : 'returned');
const methodChip = (m) => <Chip size="small" color={/ach|echeck/i.test(m || '') ? 'info' : 'default'} variant="outlined" label={m || '—'} />;

// How a needs_link candidate was matched — the Match column flags the fuzzy ones (no PaySimple id yet)
// so staff verify before Link. Parity with the AuthNet / Stripe recon.
const MATCH_LABEL = {
  payment_id: 'PaySimple ID on order', invoice: 'Invoice # = order #',
  name_amount_near: 'Donor name + amount + date', name_amount: 'Donor name + amount',
  mapped_amount_date: 'Mapped devotee + amount', amount_date: 'Amount + date',
};
const matchLabelOf = (r) => MATCH_LABEL[r.match_method] || '—';
const isFuzzyMatch = (m) => m === 'name_amount_near' || m === 'name_amount' || m === 'amount_date';

const link = ({ row, orderId }) => post('/api/manage/paysimple-reconcile/link', { payment_id: row.payment_id, order_id: orderId });
const peekCtxOf = (r) => ({ orderId: r.matched_order_id, orderNumber: r.matched_order_number, paymentId: r.payment_id, row: r, canLink: r.status === 'needs_link' });

function createOrder(r, ctx) {
  if (ctx.mapFor(r)?.splits?.length) { ctx.openCreate(r); return; }
  ctx.setToast('This subscription is not mapped yet — map it on the Subscriptions tab first.');
}

const paysimpleConfig = {
  key: 'paysimple',
  overline: 'Payments · PaySimple',
  provider: 'paysimple',
  title: 'Tx Reconciliation',
  sectionNav: { active: 'reconciliation', items: PAYSIMPLE_NAV_ITEMS },
  filters: { period: true, account: false, search: true },
  perPage: 25,
  runTooltip: 'Sync now — pull recent PaySimple payments and re-check open items against orders',

  list: ({ year, month }) => get('/api/manage/paysimple-reconcile', { year, month }),
  run: async () => { const r = await post('/api/manage/paysimple-reconcile/run', {}); return `Reconciled ${r?.scanned ?? 0} payments`; },
  rowKey: (r) => r.payment_id,
  bucketOf,
  searchText: (r, ctx) => `${r.donor_name || ''} ${r.payment_id} ${r.schedule_id || ''} ${r.matched_order_number || ''} ${r.amount_num} ${ctx.mapFor(r)?.customer_name || ''}`,

  loadMaps: async () => {
    const r = await get('/api/manage/paysimple-schedules/maps');
    const o = {}; (r?.mappings || []).forEach((x) => { o[String(x.schedule_id)] = x; });
    return o;
  },
  mapKey: (r) => (r.schedule_id ? String(r.schedule_id) : '__none__'),

  buckets: [
    { key: 'no_order', label: 'No order' },
    { key: 'order', label: 'Order' },
    { key: 'returned', label: 'Returned' },
    { key: 'all', label: 'All' },
  ],
  tabNote: (tab) => (tab === 'returned'
    ? 'Returned, refunded, voided, or failed — the money didn’t settle even if an order reads as paid. Review each and follow up.'
    : null),

  statTiles: (data) => {
    const c = data?.counts || {}; const a = data?.amounts || {};
    const returnedTotal = (c.returned ?? 0) + (c.refunded ?? 0) + (c.voided ?? 0) + (c.failed ?? 0);
    const returnedAmt = (a.returned ?? 0) + (a.refunded ?? 0) + (a.failed ?? 0);
    return [
      { label: 'PaySimple payments', value: data?.total ?? '—', sub: 'in this period · click for all', color: null, tabKey: 'all' },
      { label: 'Order', value: (c.matched ?? 0) + (c.needs_link ?? 0), sub: 'has an order', color: 'success', tabKey: 'order' },
      { label: 'No order · at risk', value: c.unbooked ?? 0, sub: fmtMoney(a.unbooked), color: 'error', tabKey: 'no_order' },
      { label: 'Returned / failed', value: returnedTotal, sub: fmtMoney(returnedAmt), color: 'warning', tabKey: 'returned' },
    ];
  },

  link,
  orderPeek: {
    link: (peekCtx) => link({ row: peekCtx.row, orderId: peekCtx.orderId }),
    idMetaKeys: ['paysimple_payment_id'],
    idLabel: 'PaySimple id on order',
  },

  columns: [
    { key: 'date', header: 'Date', width: '11%', render: (r) => <Box sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</Box> },
    {
      key: 'amount', header: 'Amount', width: '10%', align: 'right',
      render: (r) => <Box sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, color: r.status === 'unbooked' ? 'error.main' : (bucketOf(r.status) === 'returned' ? 'warning.dark' : 'inherit') }}>{fmtMoney(r.amount_num)}</Box>,
    },
    {
      key: 'donor', header: 'Donor', width: '24%',
      render: (r, ctx) => (
        <>
          <DonorLink name={r.donor_name} customerId={ctx.mapFor(r)?.customer_id} />
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontFamily: 'monospace', wordBreak: 'break-all' }}>{r.payment_id}</Typography>
        </>
      ),
    },
    { key: 'method', header: 'Method', width: '10%', render: (r) => methodChip(r.method) },
    {
      key: 'subscription', header: 'Subscription', width: '13%',
      render: (r, ctx) => {
        if (!r.schedule_id) return <Typography variant="caption" sx={{ color: 'text.secondary' }}>—</Typography>;
        const m = ctx.mapFor(r);
        return (
          <>
            <Chip size="small" variant="outlined" color="secondary" label={`Sub ${r.schedule_id}`} />
            {m && <Typography variant="caption" sx={{ display: 'block', color: 'info.main', mt: 0.25 }}>↳ {m.customer_name || 'mapped'}</Typography>}
          </>
        );
      },
    },
    {
      key: 'match', header: 'Match', width: '10%',
      render: (r) => (isFuzzyMatch(r.match_method)
        ? <Typography variant="caption" sx={{ color: 'warning.dark' }}>{matchLabelOf(r)}</Typography>
        : null),
    },
    {
      key: 'order', header: 'Order?', width: '14%',
      render: (r, ctx) => {
        if (bucketOf(r.status) === 'order') {
          return (
            <Chip size="small" color={r.status === 'matched' ? 'success' : 'warning'} variant={r.status === 'matched' ? 'outlined' : 'filled'}
              label={`Order ${r.matched_order_number || ''}`.trim()}
              onClick={r.matched_order_id ? () => ctx.openOrderPeek(peekCtxOf(r)) : undefined}
              sx={r.matched_order_id ? { cursor: 'pointer' } : undefined} />
          );
        }
        if (bucketOf(r.status) === 'returned') {
          return <Chip size="small" color="warning" variant="outlined" label={r.status === 'returned' ? `Returned${r.returned_reason ? ` · ${r.returned_reason}` : ''}` : r.status} />;
        }
        return <Chip size="small" color="error" variant="filled" label="No order" />;
      },
    },
    {
      key: 'actions', header: 'Action', width: '12%', align: 'right',
      render: (r, ctx) => (
        <Box sx={{ whiteSpace: 'nowrap' }}>
          {r.status === 'unbooked' && <Button size="small" variant="contained" onClick={() => createOrder(r, ctx)}>Create order</Button>}
          {r.status === 'needs_link' && <Button size="small" variant="outlined" onClick={() => ctx.linkRow(r)}>Link</Button>}
        </Box>
      ),
    },
  ],

  renderDialogs: (ctx) => (
    <PaysimpleCreateOrderDialog
      row={ctx.slots.createRow}
      mapping={ctx.slots.createRow ? ctx.mapFor(ctx.slots.createRow) : null}
      onClose={ctx.closeCreate}
      onCreated={(res) => { ctx.closeCreate(); ctx.setToast(res?.order_number ? `Order ${res.order_number} created` : 'Order created'); ctx.reload(); }}
    />
  ),
};

export default paysimpleConfig;
